import { useEffect, useState, type ReactNode } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Input } from "./ui/input";
import { Field, FieldGroup, FieldLabel } from "./ui/field";
import { cn } from "../lib/utils";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  requireText?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

// ConfirmDialog is a thin wrapper around shadcn's AlertDialog that adds
// support for a "type-to-confirm" field used by destructive actions like
// project deletion.
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive,
  busy,
  requireText,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const [confirmation, setConfirmation] = useState("");

  useEffect(() => {
    if (open) setConfirmation("");
  }, [open]);

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next: boolean) => {
        if (!next) onCancel();
      }}
    >
      <AlertDialogContent data-testid="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{message}</AlertDialogDescription>
        </AlertDialogHeader>
        {requireText ? (
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="confirm-text">
                Type <code className="font-mono rounded bg-accent px-1">{requireText}</code>{" "}
                to confirm
              </FieldLabel>
              <Input
                id="confirm-text"
                autoComplete="off"
                className="font-mono"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
              />
            </Field>
          </FieldGroup>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            disabled={busy || (!!requireText && confirmation !== requireText)}
            data-testid="confirm-action"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              onConfirm();
            }}
            className={cn(
              destructive ? "bg-destructive text-white" : "bg-primary text-primary-foreground",
            )}
          >
            {busy ? "Working…" : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}